import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { SaveAsDraftModal } from '@frontend/unsaved-changes-modal';
import { theme } from '@frontend/theme';
import { Chip, Heading } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import { useBulkBreadcrumbs } from '../hooks/use-bulk-breadcrumbs';
import { useBulkNavigator } from '../hooks/use-bulk-navigator';
import { BulkEmailComposeView } from './bulk-email-compose-view/bulk-email-compose-view';
import { BulkEmailComposer } from './bulk-email-composer/bulk-email-composer';
import { BulkEmailLanding } from './bulk-email-landing';
import { BulkEmailTemplates } from './bulk-email-templates/bulk-email-templates';
import { BulkMessageEditorFooter } from './bulk-message-editor-footer';
import { BulkMessagePage } from './bulk-message-page';
import { CampaignEditor } from './campaign-editor/campaign-editor';
import { useDraft } from './campaign-editor/hooks/use-draft';

export const BulkMessageEditorPage = () => {
  const { t } = useTranslation('bulk-messaging');
  const { campaignType, unsavedChangesProps, view } = useBulkMessageEditorContext();
  const breadcrumbs = useBulkBreadcrumbs();
  const navigateTo = useBulkNavigator(campaignType);
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  // TODO: When org-level location controls land (@kylirh), compose header chips and any related
  // location lists should reflect the full organization (not only the global location selector). Confirm
  // feature-flag strategy: ANY vs ALL selected locations — from the campaign vs from the organization.
  const isNewComposeEmailEnabled = getFeatureFlagValue('bulk-messaging-compose-email-revamp');
  const { selectedLocationIds: appSelectedLocationIds, getLocationName, isSingleTypeScope } = useAppScopeStore();

  const { saveDraft } = useDraft();
  const { modalProps, proceed } = unsavedChangesProps;

  const handleCancel = () => {
    modalProps.onClose();
  };

  const handleDelete = () => {
    modalProps.onClose();
    proceed();
  };

  const handleSave = () => {
    saveDraft();
    modalProps.onClose();
    proceed();
  };

  const { locationIds: contextLocationIds } = useBulkMessageEditorContext();
  const selectedLocationIds = contextLocationIds.length ? contextLocationIds : appSelectedLocationIds;

  const showComposeLocationChips = !isSingleTypeScope && selectedLocationIds.length > 0;

  return (
    <>
      {view === 'new-email' &&
        (isNewComposeEmailEnabled ? (
          <BulkMessagePage
            title={
              <div css={titleRowStyle}>
                <Heading level={2}>{t('Compose Email')}</Heading>
                {showComposeLocationChips &&
                  (selectedLocationIds.length === 1 ? (
                    <Chip.SingleChip>{getLocationName(selectedLocationIds[0] ?? '')}</Chip.SingleChip>
                  ) : (
                    <Chip.MultiChip>{t('{{count}} Locations', { count: selectedLocationIds.length })}</Chip.MultiChip>
                  ))}
              </div>
            }
            breadcrumbs={breadcrumbs.newEmail}
            css={emailComposerPageStyle}
            maxWidth='unset'
          >
            <BulkEmailComposeView />
            <BulkMessageEditorFooter onBackClick={navigateTo.editor} />
          </BulkMessagePage>
        ) : (
          <BulkMessagePage
            title={<Heading level={3}>{t('Select How You Would Like to Design Your Email')}</Heading>}
            subtitle={t('Start new or use a template to construct your new email campaign.')}
            breadcrumbs={breadcrumbs.newEmail}
            css={customPageHeaderStyle}
            maxWidth='unset'
          >
            <BulkEmailLanding />
            <BulkMessageEditorFooter onBackClick={navigateTo.editor} />
          </BulkMessagePage>
        ))}

      {view === 'templates' && (
        <BulkMessagePage breadcrumbs={breadcrumbs.templates} css={[customPageHeaderStyle, { height: '100%' }]}>
          <BulkEmailTemplates />
          <BulkMessageEditorFooter onBackClick={navigateTo.new} />
        </BulkMessagePage>
      )}

      {view === 'compose' && (
        <BulkMessagePage
          maxWidth='unset'
          css={{
            padding: '0 !important',
            '.page-header': {
              marginBottom: 0,
              '& > div': {
                marginBottom: 0,
              },
            },
          }}
        >
          <BulkEmailComposer />
        </BulkMessagePage>
      )}

      {view === 'editor' && (
        <BulkMessagePage
          breadcrumbs={breadcrumbs.stepper}
          css={{
            maxWidth: 800,
            ...customPageHeaderStyle,
          }}
        >
          <CampaignEditor />
        </BulkMessagePage>
      )}

      <SaveAsDraftModal
        message={t('If you leave now your work may not be saved. To come back to this later select "Save as Draft".')}
        modalProps={modalProps}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onSaveDraft={handleSave}
      />
    </>
  );
};

const titleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
};

const customPageHeaderStyle = {
  '.page-header': {
    margin: 0,
  },
};

const emailComposerPageStyle = {
  '&': {
    padding: '24px 0',
  },
  '.page-header': {
    padding: '0 24px',
  },
  '.page-header nav': {
    marginBottom: '16px',
  },
  '.page-header > div': {
    margin: '24px 0 0',
  },
  '.page-header h2': {
    lineHeight: '28px',
  },
};
